import { useState } from "react";
import { Link } from "react-router-dom";
import { Cookie, ShieldCheck, Settings2 } from "lucide-react";
import { useConsent, acceptAll, rejectAll, saveConsent, closeCookiePreferences } from "../lib/consent";

const Toggle = ({ checked, onChange, disabled, label, desc, testid }) => (
    <label className={`flex items-start justify-between gap-4 rounded-2xl border border-slate-200 p-4 ${disabled ? "bg-slate-50" : "bg-white"}`}>
        <span>
            <span className="block text-sm font-bold text-[#0B1F3A]">{label}</span>
            <span className="mt-1 block text-xs leading-relaxed text-[#4F5D70]">{desc}</span>
        </span>
        <input
            type="checkbox"
            checked={checked}
            disabled={disabled}
            onChange={(e) => onChange?.(e.target.checked)}
            data-testid={testid}
            className="mt-1 h-5 w-5 shrink-0 accent-[#E30613]"
        />
    </label>
);

export const CookieBanner = () => {
    const { consent, panelOpen } = useConsent();
    const [custom, setCustom] = useState(false);
    const [media, setMedia] = useState(consent.media);
    const [analytics, setAnalytics] = useState(consent.analytics);

    const visible = !consent.decided || panelOpen;
    if (!visible) return null;

    const showCustom = custom || panelOpen;
    const btn = "flex-1 rounded-full px-5 py-3 text-sm font-bold transition-colors";

    return (
        <div
            className="fixed inset-x-0 bottom-0 z-[90] p-3 sm:p-5"
            role="dialog"
            aria-modal="true"
            aria-label="Preferenze cookie"
            data-testid="cookie-banner"
        >
            <div className="mx-auto max-w-3xl rounded-3xl border border-slate-200 bg-white p-5 shadow-[0_32px_80px_-24px_rgba(11,31,58,0.45)] sm:p-7">
                <div className="flex items-start gap-4">
                    <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-[#0B1F3A] text-white">
                        <Cookie className="h-5 w-5" />
                    </span>
                    <div className="min-w-0 flex-1">
                        <h2 className="font-display text-lg font-extrabold text-[#0B1F3A]">
                            Rispettiamo la tua privacy
                        </h2>
                        <p className="mt-1.5 text-sm leading-relaxed text-[#4F5D70]">
                            Usiamo cookie tecnici necessari al funzionamento del sito e, solo con il
                            tuo consenso, contenuti di terze parti (video YouTube) e statistiche
                            anonime. Puoi cambiare idea in qualsiasi momento da "Preferenze cookie"
                            nel footer.{" "}
                            <Link to="/cookie-policy" className="font-semibold text-[#E30613] underline-offset-2 hover:underline" data-testid="cookie-banner-policy-link">
                                Cookie Policy
                            </Link>{" "}
                            ·{" "}
                            <Link to="/privacy-policy" className="font-semibold text-[#E30613] underline-offset-2 hover:underline">
                                Privacy Policy
                            </Link>
                        </p>
                    </div>
                </div>

                {showCustom && (
                    <div className="mt-5 space-y-3" data-testid="cookie-custom-panel">
                        <Toggle checked disabled label="Cookie necessari" desc="Indispensabili per il funzionamento del sito (es. memorizzazione di questa scelta). Sempre attivi." testid="cookie-toggle-necessary" />
                        <Toggle checked={media} onChange={setMedia} label="Contenuti esterni (video YouTube)" desc="Permette di caricare i video incorporati da youtube-nocookie.com. Google può impostare cookie e trattare dati secondo la propria policy." testid="cookie-toggle-media" />
                        <Toggle checked={analytics} onChange={setAnalytics} label="Statistiche" desc="Misurazione anonima delle visite per migliorare il sito. Attualmente nessun servizio di statistica è attivo." testid="cookie-toggle-analytics" />
                    </div>
                )}

                <div className="mt-5 flex flex-col gap-2.5 sm:flex-row">
                    <button type="button" onClick={acceptAll} data-testid="cookie-accept-button" className={`${btn} bg-[#0B1F3A] text-white hover:bg-[#132b4f]`}>
                        Accetta
                    </button>
                    <button type="button" onClick={rejectAll} data-testid="cookie-reject-button" className={`${btn} border-2 border-[#0B1F3A] text-[#0B1F3A] hover:bg-slate-50`}>
                        Rifiuta
                    </button>
                    {showCustom ? (
                        <button
                            type="button"
                            onClick={() => saveConsent({ media, analytics })}
                            data-testid="cookie-save-button"
                            className={`${btn} inline-flex items-center justify-center gap-2 border-2 border-[#E30613] text-[#E30613] hover:bg-[#E30613]/5`}
                        >
                            <ShieldCheck className="h-4 w-4" /> Salva preferenze
                        </button>
                    ) : (
                        <button
                            type="button"
                            onClick={() => setCustom(true)}
                            data-testid="cookie-customize-button"
                            className={`${btn} inline-flex items-center justify-center gap-2 border-2 border-slate-300 text-[#0B1F3A] hover:border-[#0B1F3A]`}
                        >
                            <Settings2 className="h-4 w-4" /> Personalizza
                        </button>
                    )}
                </div>
                {panelOpen && consent.decided && (
                    <button type="button" onClick={closeCookiePreferences} className="mt-3 text-xs text-[#4F5D70] underline-offset-2 hover:underline" data-testid="cookie-close-button">
                        Chiudi senza modificare
                    </button>
                )}
            </div>
        </div>
    );
};
